import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/clerk-react";
import toast from "react-hot-toast";
import TopBar from "../../components/topBar/topBar";
import Result from "../../components/result/Result";
import { apiClient } from "../../api/apiClient";
import { getCitation } from "../../helpers/citation.helper";

/* ── Icons ───────────────────────────────────────────────────────────────── */
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ── Confirmation modal ──────────────────────────────────────────────────── */
function ConfirmModal({ title, body, confirmLabel, cancelLabel, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={onCancel}
        style={{ animation: "fadeIn 0.15s ease both" }}
      />
      <div
        className="relative bg-mainWhite rounded-2xl shadow-search w-full max-w-sm p-6"
        style={{ animation: "modalIn 0.18s ease both" }}
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 p-1.5 rounded-xl text-mainDark/30 hover:text-mainDark hover:bg-mainDark/6 transition-all duration-150"
        >
          <span className="block h-4 w-4"><XIcon /></span>
        </button>

        <h3 className="text-lg font-normal text-mainDark pr-8">{title}</h3>
        {body && <p className="mt-2 text-sm font-light text-mainDark/50 leading-relaxed">{body}</p>}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm font-normal text-mainDark/50 hover:text-mainDark hover:bg-mainDark/6 transition-all duration-150"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl text-sm font-normal bg-red-500 text-white hover:bg-red-600 transition-all duration-150"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────────────────── */
function Projects() {
  const { t } = useTranslation();
  const { isLoaded, user } = useUser();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await apiClient.get("/project?populate=true");
      const data = res.data?.data || [];
      setProjects(data);
      return data;
    } catch (e) {
      setError(e.response?.data?.message || t("profile.couldNotLoad"));
      return [];
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isLoaded || !user) return;
    load().then((data) => {
      if (data.length > 0) setSelectedId((id) => id ?? data[0]._id);
    });
  }, [isLoaded, user, load]);

  const reload = useCallback(async () => {
    const data = await load();
    setSelectedId((id) =>
      data.find((p) => p._id === id) ? id : (data[0]?._id ?? null)
    );
  }, [load]);

  const startRename = (p) => { setEditingId(p._id); setEditName(p.name); };

  const submitRename = async (projectId) => {
    try {
      await apiClient.put(`/project/${projectId}`, { name: editName.trim() });
      setEditingId(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || t("profile.renameFailed"));
    }
  };

  const createProject = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await apiClient.post("/project", { name });
      setIsCreating(false);
      setNewName("");
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || t("projectsModal.createFailed"));
    }
  };

  const deleteProject = async () => {
    if (!confirmDelete) return;
    try {
      await apiClient.delete(`/project/${confirmDelete._id}`);
      setConfirmDelete(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || t("profile.deleteFailed"));
      setConfirmDelete(null);
    }
  };

  const removeBook = async (projectId, bookId) => {
    try {
      await apiClient.delete(`/project/${projectId}/book/${bookId}`);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || t("profile.couldNotRemoveBook"));
    }
  };

  const selectedProject = projects.find((p) => p._id === selectedId) ?? null;

  const exportToCSV = () => {
    if (!selectedProject) return;
    const books = selectedProject.books || [];
    if (books.length === 0) return;

    const esc = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

    const stripHtml = (html) => {
      if (!html) return "";
      const el = document.createElement("div");
      el.innerHTML = html;
      return (el.textContent || "").replace(/\u00a0/g, " ").trim();
    };

    const typeLabel = (type) => ({
      1: "Monograph",
      2: "Zborník (Primary)",
      3: "Zborník (Secondary)",
      4: "Study",
      5: "Periodikum",
      6: "Internet",
      7: "Archive",
    }[type] || "");

    const headers = [
      "Type", "Title", "Authors", "Year", "Publisher",
      "City", "Pages", "ISBN/ISSN", "URL", "Keywords", "Citation",
    ];

    const rows = books.map((book) => {
      const authorsText = (book.authors || []).join(" - ") || book.archive || "";
      const pages =
        book.total_pages || book.full_pages ||
        (book.pageFrom && book.pageTo ? `${book.pageFrom}–${book.pageTo}` : "");
      const citation = stripHtml(getCitation(book));

      return [
        esc(typeLabel(book.citation_type)),
        esc(book.title || book.trust || ""),
        esc(authorsText),
        esc(book.year || ""),
        esc(book.publisher || ""),
        esc(book.city || ""),
        esc(pages),
        esc(book.isbn || ""),
        esc(book.url || ""),
        esc((book.keywords || []).join(", ")),
        esc(citation),
      ].join(",");
    });

    const csv = [headers.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const projectName = selectedProject.isDefault
      ? t("common.uncategorizedProjectName")
      : selectedProject.name;
    a.href = url;
    a.download = `${projectName.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t("projects.exportSuccess"));
  };

  if (!isLoaded || !user) return null;

  return (
    <>
      {confirmDelete && (
        <ConfirmModal
          title={t("profile.confirmDeleteProject", {
            name: confirmDelete.isDefault
              ? t("common.uncategorizedProjectName")
              : confirmDelete.name,
          })}
          body="This will permanently remove the project. Saved references will not be deleted."
          confirmLabel={t("profile.deleteProject")}
          cancelLabel={t("common.cancel")}
          onConfirm={deleteProject}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* ── Full-height layout: sidebar + right panel ── */}
      <div className="flex" style={{ height: "100dvh" }}>

        {/* ══════════════ LEFT SIDEBAR (full height, fixed dark) ══════════════ */}
        <aside className="hidden md:flex w-80 xl:w-96 shrink-0 flex-col" style={{ backgroundColor: "rgb(40, 38, 44)" }}>

          {/* Sidebar header */}
          <div className="px-6 pt-8 pb-6 shrink-0">
            <h1 className="text-2xl font-extralight text-white/90 tracking-tight">
              {t("projects.heading")}
            </h1>
            {!loading && projects.length > 0 && (
              <p className="mt-1.5 text-[11px] text-white/25 font-light">
                {t("projects.stats", {
                  projects: projects.length,
                  books: projects.reduce((s, p) => s + (p.books || []).length, 0),
                })}
              </p>
            )}
          </div>

          <div className="mx-6 h-px bg-white/[0.07] shrink-0" />

          {/* Project list */}
          <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
            {loading ? (
              [0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse flex items-center gap-3 px-3 py-3 rounded-xl">
                  <div className="h-9 w-9 rounded-lg bg-white/[0.07] shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-3/4 rounded-full bg-white/[0.07]" />
                    <div className="h-2 w-10 rounded-full bg-white/[0.04]" />
                  </div>
                </div>
              ))
            ) : projects.length === 0 ? (
              <p className="px-3 py-4 text-sm text-white/50 font-light">
                {t("projects.noProjects")}
              </p>
            ) : (
              projects.map((p, i) => {
                const isSelected = selectedId === p._id;
                const isEditing = editingId === p._id;
                const initial = p.isDefault ? "★" : (p.name?.[0] || "?").toUpperCase();
                const books = p.books || [];

                return (
                  <div
                    key={p._id}
                    className={`group relative flex items-center gap-3 rounded-xl px-3 py-3 cursor-pointer transition-all duration-150
                      ${isSelected ? "bg-mainBlue" : "hover:bg-white/[0.06]"}`}
                    onClick={() => !isEditing && setSelectedId(p._id)}
                    style={{ animation: `sidebarIn 0.22s ease both ${i * 30}ms` }}
                  >
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold select-none transition-all duration-150
                      ${isSelected
                        ? "bg-ink/15 text-ink"
                        : "bg-white/[0.08] text-white/40 group-hover:bg-white/[0.13] group-hover:text-white/70"
                      }`}
                    >
                      {initial}
                    </div>

                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <input
                          autoFocus
                          className="w-full bg-white/10 rounded-md px-2 py-0.5 text-sm text-white outline-none border border-white/20 focus:border-mainBlue"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.stopPropagation(); submitRename(p._id); }
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <>
                          <p className={`text-sm font-normal truncate leading-snug transition-colors duration-150
                            ${isSelected ? "text-ink" : "text-white/80 group-hover:text-white"}`}
                          >
                            {p.isDefault ? t("common.uncategorizedProjectName") : p.name}
                          </p>
                          <p className={`text-[11px] leading-none mt-0.5 font-light transition-colors duration-150
                            ${isSelected ? "text-ink/50" : "text-white/45 group-hover:text-white/65"}`}
                          >
                            {books.length} {books.length === 1 ? "item" : "items"}
                          </p>
                        </>
                      )}
                    </div>

                    {!p.isDefault && !isEditing && (
                      <button
                        type="button"
                        title={t("profile.rename")}
                        className={`shrink-0 p-1.5 rounded-lg transition-all duration-150 opacity-0 group-hover:opacity-100
                          ${isSelected
                            ? "text-ink/40 hover:text-ink hover:bg-ink/10"
                            : "text-white/35 hover:text-white hover:bg-white/10"
                          }`}
                        onClick={(e) => { e.stopPropagation(); startRename(p); }}
                      >
                        <span className="block h-3.5 w-3.5"><EditIcon /></span>
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </nav>

          {/* Create new project */}
          <div className="px-3 pb-5 shrink-0">
            <div className="h-px bg-white/[0.07] mb-3 mx-1" />
            {isCreating ? (
              <div className="flex items-center gap-2 px-1">
                <input
                  autoFocus
                  className="flex-1 min-w-0 bg-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/25 outline-none border border-white/10 focus:border-mainBlue transition-colors duration-150"
                  placeholder={t("projectsModal.projectNamePlaceholder")}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createProject();
                    if (e.key === "Escape") { setIsCreating(false); setNewName(""); }
                  }}
                />
                <button
                  type="button"
                  onClick={createProject}
                  className="shrink-0 px-3 py-2 rounded-xl bg-mainBlue text-ink text-xs font-medium hover:brightness-105 transition-all duration-150"
                >
                  {t("common.save")}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsCreating(false); setNewName(""); }}
                  className="shrink-0 p-2 rounded-xl text-white/30 hover:text-white/70 transition-colors duration-150"
                >
                  <span className="block h-3.5 w-3.5"><XIcon /></span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.10] transition-all duration-150"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/[0.12] text-white">
                  <span className="block h-4 w-4"><PlusIcon /></span>
                </span>
                <span className="text-sm font-light text-white/80">{t("projectsModal.createNewProject")}</span>
              </button>
            )}
          </div>
        </aside>

        {/* ══════════════ RIGHT PANEL (TopBar + content) ══════════════ */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <TopBar logo={true} />

          <main className="flex-1 overflow-y-auto">

            {/* Mobile: horizontal pill tabs + create */}
            <div className="md:hidden border-b border-mainDark/8">
              <div className="overflow-x-auto scrollbar-none">
                <div className="flex gap-2 px-4 pt-3 pb-2 w-max">
                  {projects.map((p) => (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => setSelectedId(p._id)}
                      className={`px-4 py-1.5 rounded-full text-sm font-normal whitespace-nowrap transition-all duration-150
                        ${selectedId === p._id
                          ? "bg-mainDark text-mainWhite"
                          : "bg-mainDark/6 text-mainDark/50 hover:bg-mainDark/12"
                        }`}
                    >
                      {p.isDefault ? t("common.uncategorizedProjectName") : p.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-4 pb-3">
                {isCreating ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      className="flex-1 min-w-0 rounded-xl border border-mainDark/15 bg-mainWhite px-3 py-2 text-sm text-mainDark placeholder-mainDark/30 outline-none focus:border-mainBlue transition-colors duration-150"
                      placeholder={t("projectsModal.projectNamePlaceholder")}
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") createProject();
                        if (e.key === "Escape") { setIsCreating(false); setNewName(""); }
                      }}
                    />
                    <button type="button" onClick={createProject} className="px-3 py-2 rounded-xl bg-mainDark text-mainWhite text-sm">{t("common.save")}</button>
                    <button type="button" onClick={() => { setIsCreating(false); setNewName(""); }} className="p-2 rounded-xl text-mainDark/30 hover:text-mainDark"><span className="block h-4 w-4"><XIcon /></span></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsCreating(true)}
                    className="flex items-center gap-1.5 text-xs font-medium text-mainDark/35 hover:text-mainDark transition-colors duration-150"
                  >
                    <span className="block h-3.5 w-3.5"><PlusIcon /></span>
                    {t("projectsModal.createNewProject")}
                  </button>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="mx-6 mt-6 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-500">
                {error}
              </p>
            )}

            {/* No projects */}
            {!loading && projects.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-mainDark/5 text-mainDark/25">
                  <span className="h-8 w-8"><BookIcon /></span>
                </div>
                <p className="text-lg font-light text-mainDark/30">{t("projects.noProjects")}</p>
                <Link
                  to="/"
                  className="mt-3 text-sm text-mainBlue underline underline-offset-2 hover:text-mainDark transition-colors duration-150"
                >
                  {t("projects.startSearching")}
                </Link>
              </div>
            )}

            {/* Selected project */}
            {selectedProject && (
              <div
                className="px-6 md:px-10 xl:px-14 py-8 md:py-10 max-w-4xl"
                key={selectedProject._id}
                style={{ animation: "contentIn 0.2s ease both" }}
              >
                {/* Project header */}
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-mainBlue/20 text-mainDark text-xl font-semibold select-none">
                    {selectedProject.isDefault ? "★" : (selectedProject.name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h2 className="text-2xl md:text-3xl font-extralight tracking-tight text-mainDark">
                        {selectedProject.isDefault
                          ? t("common.uncategorizedProjectName")
                          : selectedProject.name}
                      </h2>
                      {selectedProject.isDefault && (
                        <span className="rounded-full bg-mainBlue/20 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-mainDark/60 font-medium">
                          {t("profile.defaultBadge")}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm font-light text-mainDark/35">
                      {t("profile.itemCount", { count: (selectedProject.books || []).length })}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {(selectedProject.books || []).length > 0 && (
                      <button
                        type="button"
                        onClick={exportToCSV}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-normal text-mainDark/40 hover:text-mainBlue hover:bg-mainBlue/8 transition-all duration-150"
                      >
                        <span className="h-4 w-4"><DownloadIcon /></span>
                        <span className="hidden sm:inline">{t("projects.exportCSV")}</span>
                      </button>
                    )}
                    {!selectedProject.isDefault && (
                      <button
                        type="button"
                        title={t("profile.deleteProject")}
                        onClick={() => setConfirmDelete(selectedProject)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-normal text-mainDark/30 hover:text-red-500 hover:bg-red-500/8 transition-all duration-150"
                      >
                        <span className="h-4 w-4"><TrashIcon /></span>
                        <span className="hidden sm:inline">{t("profile.deleteProject")}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-6 mb-8 h-px bg-mainDark/8" />

                {/* Books */}
                {(selectedProject.books || []).length === 0 ? (
                  <div className="py-16 text-center">
                    <div className="mb-4 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-mainDark/5 text-mainDark/20">
                      <span className="h-7 w-7"><BookIcon /></span>
                    </div>
                    <p className="text-mainDark/30 font-light">{t("profile.emptyProject")}</p>
                    <Link
                      to="/"
                      className="mt-3 inline-block text-sm text-mainBlue underline underline-offset-2 hover:text-mainDark transition-colors duration-150"
                    >
                      {t("projects.startSearching")}
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(selectedProject.books || []).map((book) => (
                      <div key={book._id} className="relative group/book">
                        {/* Remove button — inside the card (absolute top-right) */}
                        <button
                          type="button"
                          onClick={() => removeBook(selectedProject._id, book._id)}
                          className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full border border-mainDark/15 bg-mainWhite px-2.5 py-1 text-xs text-mainDark/40 hover:border-red-300 hover:bg-red-50 hover:text-red-500 transition-all duration-150"
                        >
                          <span className="h-3 w-3"><TrashIcon /></span>
                          {t("profile.remove")}
                        </button>
                        <Result bookData={book} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      <style>{`
        @keyframes sidebarIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes contentIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .scrollbar-none { scrollbar-width: none; }
        .scrollbar-none::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
}

export default Projects;
